import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import LoadIndicator from 'devextreme-react/load-indicator';
import { listItems } from '../fileBrowser/fileBrowserApi';
import {
  fileAccessRestrictedHref,
  isFileAccessRestrictedStatus
} from '../storageAccess/fileAccessRestrictedUrls';
import { MediaViewerChromeBar } from './MediaViewerChromeBar';
import { displayNameFromPath } from './mediaFileTypes';
import type { MediaKind } from './mediaFileTypes';
import { useMediaFileMetadata } from './mediaViewerMetadata';
import { mediaPreviewSrc } from './mediaPreviewUrls';
import { useMediaImageDisplaySrc } from './useMediaImageDisplaySrc';
import { useMediaVideoLoadState } from './useMediaVideoLoadState';
import { FilePreviewPopupShell } from './FilePreviewPopupShell';
import { MediaGalleryPane } from './MediaGalleryPane';
import {
  buildImagePlaylist,
  parentDirectoryPath,
  playlistNext,
  playlistPrev,
  type MediaGalleryItem
} from './mediaGalleryPlaylist';
import { useImagePanZoom } from './useImagePanZoom';
import {
  readGalleryViewMode,
  readThumbSize,
  writeGalleryViewMode,
  type GalleryViewMode,
  type ThumbSize
} from './viewerOpenPreference';
import './MediaPreviewPopup.css';
import './mediaViewerStage.css';

export interface MediaPreviewState {
  open: boolean;
  kind: MediaKind;
  mountId: string;
  relativePath: string;
  /** Optional sibling images from the file browser folder context. */
  playlist?: readonly MediaGalleryItem[];
}

interface MediaPreviewPopupProps {
  state: MediaPreviewState | null;
  onClose: () => void;
}

export function MediaPreviewPopup({ state, onClose }: MediaPreviewPopupProps) {
  const navigate = useNavigate();
  const open = state?.open ?? false;
  const kind = state?.kind ?? 'image';
  const mountId = state?.mountId ?? '';
  const relativePath = state?.relativePath ?? '';

  const [activePath, setActivePath] = useState(relativePath);
  const [playlist, setPlaylist] = useState<readonly MediaGalleryItem[]>(state?.playlist ?? []);
  const [galleryViewMode, setGalleryViewMode] = useState<GalleryViewMode>(readGalleryViewMode);
  const [thumbSize] = useState<ThumbSize>(readThumbSize);

  useEffect(() => {
    if (!open) return;
    setActivePath(relativePath);
    setPlaylist(state?.playlist ?? []);
    setGalleryViewMode(readGalleryViewMode());
  }, [open, relativePath, state?.playlist]);

  useEffect(() => {
    if (!open || kind !== 'image' || !mountId || !relativePath) return;
    if (state?.playlist && state.playlist.length > 0) return;

    let cancelled = false;
    const parent = parentDirectoryPath(relativePath);
    listItems(mountId, parent || undefined)
      .then((items) => {
        if (!cancelled) setPlaylist(buildImagePlaylist(items));
      })
      .catch(() => {
        /* single-image fallback */
      });

    return () => {
      cancelled = true;
    };
  }, [open, kind, mountId, relativePath, state?.playlist]);

  const imageDisplay = useMediaImageDisplaySrc(mountId, activePath, open && kind === 'image');
  const imageSrc = imageDisplay.displaySrc;
  const panZoom = useImagePanZoom(open && kind === 'image');
  const { reset: resetPanZoom } = panZoom;

  useEffect(() => {
    if (!open || kind !== 'image') return;
    resetPanZoom();
  }, [activePath, kind, open, resetPanZoom]);

  useEffect(() => {
    if (!open || kind !== 'image' || imageDisplay.state !== 'error') return;
    if (imageDisplay.errorStatus != null && isFileAccessRestrictedStatus(imageDisplay.errorStatus)) {
      onClose();
      navigate(fileAccessRestrictedHref(), { replace: true });
    }
  }, [open, kind, imageDisplay.state, imageDisplay.errorStatus, navigate, onClose]);

  const videoSrc = useMemo(() => {
    if (!open || kind !== 'video' || !mountId || !activePath) return '';
    return mediaPreviewSrc(mountId, activePath);
  }, [open, kind, mountId, activePath]);

  const onVideoAccessRestricted = useCallback(() => {
    onClose();
    navigate(fileAccessRestrictedHref(), { replace: true });
  }, [navigate, onClose]);

  const videoLoad = useMediaVideoLoadState(videoSrc, {
    onAccessRestricted: onVideoAccessRestricted
  });

  const [imageDecoded, setImageDecoded] = useState(false);
  const [imageDecodeFailed, setImageDecodeFailed] = useState(false);

  useEffect(() => {
    setImageDecoded(false);
    setImageDecodeFailed(false);
  }, [imageSrc]);

  const metadata = useMediaFileMetadata(mountId, activePath, open);

  const titlePrefix = kind === 'image' ? 'Image' : 'Video';
  const fileName = displayNameFromPath(activePath);
  const title = activePath ? `${titlePrefix}: ${fileName}` : `${titlePrefix} preview`;

  const loadState =
    kind === 'image'
      ? imageDisplay.state === 'error' || imageDecodeFailed
        ? 'error'
        : imageDisplay.state !== 'ready' || !imageDecoded
          ? 'loading'
          : 'loaded'
      : videoLoad.state === 'idle'
        ? 'loading'
        : videoLoad.state;

  const actionsDisabled = loadState !== 'loaded';
  const showGallery = kind === 'image' && playlist.length > 0;

  const onImageMount = useCallback((el: HTMLImageElement | null) => {
    if (el?.complete && el.naturalWidth > 0) {
      setImageDecoded(true);
    }
  }, []);

  const onSelectGalleryPath = useCallback((path: string) => {
    setActivePath(path);
  }, []);

  const onGalleryViewModeChange = useCallback((mode: GalleryViewMode) => {
    setGalleryViewMode(mode);
    writeGalleryViewMode(mode);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (kind !== 'image' || playlist.length <= 1) return;
      if (event.key === 'ArrowLeft') {
        const prev = playlistPrev(playlist, activePath);
        if (prev) {
          event.preventDefault();
          setActivePath(prev.path);
        }
      } else if (event.key === 'ArrowRight') {
        const next = playlistNext(playlist, activePath);
        if (next) {
          event.preventDefault();
          setActivePath(next.path);
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activePath, kind, open, playlist]);

  return (
    <FilePreviewPopupShell visible={open} title={title} onClose={onClose}>
      <div className="media-preview-popup__body">
        <MediaViewerChromeBar
          kind={kind}
          mountId={mountId}
          relativePath={activePath}
          metadata={metadata}
          disabled={actionsDisabled}
        />
        <div className="media-viewer-stage">
          {loadState === 'loading' ? (
            <div className="media-viewer-stage__loading" aria-busy="true" aria-live="polite">
              <LoadIndicator visible height={28} width={28} />
              <span>Loading {kind === 'image' ? 'image' : 'video'}…</span>
            </div>
          ) : null}
          {loadState === 'error' ? (
            <p className="media-viewer-stage__error">
              {kind === 'video'
                ? (videoLoad.errorMessage ?? 'Could not load this video. Check permissions or try again.')
                : (imageDisplay.errorMessage ??
                  'Could not load this file. Check permissions or try again.')}
            </p>
          ) : (
            <div
              className={`media-viewer-stage__fit${kind === 'image' ? ' media-viewer-stage__fit--pannable' : ''}`}
              onWheel={kind === 'image' ? panZoom.onWheel : undefined}
              onPointerDown={kind === 'image' ? panZoom.onPointerDown : undefined}
              onPointerMove={kind === 'image' ? panZoom.onPointerMove : undefined}
              onPointerUp={kind === 'image' ? panZoom.onPointerUp : undefined}
              onPointerCancel={kind === 'image' ? panZoom.onPointerUp : undefined}
            >
              {kind === 'image' && imageSrc && imageDisplay.state === 'ready' ? (
                <img
                  key={imageSrc}
                  ref={onImageMount}
                  className={`media-viewer-stage__img${loadState === 'loaded' ? ' media-viewer-stage__img--visible' : ''}`}
                  style={{ transform: panZoom.transform, cursor: panZoom.scale > 1 ? 'grab' : 'default' }}
                  src={imageSrc}
                  alt=""
                  draggable={false}
                  onLoad={() => setImageDecoded(true)}
                  onError={() => setImageDecodeFailed(true)}
                />
              ) : null}
              {videoSrc && kind === 'video' ? (
                <video
                  key={videoSrc}
                  ref={videoLoad.onVideoMount}
                  className="media-viewer-stage__video"
                  src={videoSrc}
                  controls
                  playsInline
                  preload="metadata"
                  onLoadedMetadata={videoLoad.onLoaded}
                  onCanPlay={videoLoad.onLoaded}
                  onError={videoLoad.onError}
                />
              ) : null}
            </div>
          )}
        </div>
        {showGallery ? (
          <MediaGalleryPane
            mountId={mountId}
            relativePath={activePath}
            playlist={playlist}
            viewMode={galleryViewMode}
            thumbSize={thumbSize}
            onSelectPath={onSelectGalleryPath}
            onViewModeChange={onGalleryViewModeChange}
            zoomIn={panZoom.zoomIn}
            zoomOut={panZoom.zoomOut}
            zoomFit={panZoom.zoomFit}
            panZoomEnabled={kind === 'image'}
          />
        ) : kind === 'image' ? (
          <MediaGalleryPane
            mountId={mountId}
            relativePath={activePath}
            playlist={[]}
            viewMode={galleryViewMode}
            thumbSize={thumbSize}
            onSelectPath={onSelectGalleryPath}
            onViewModeChange={onGalleryViewModeChange}
            zoomIn={panZoom.zoomIn}
            zoomOut={panZoom.zoomOut}
            zoomFit={panZoom.zoomFit}
            panZoomEnabled
          />
        ) : null}
      </div>
    </FilePreviewPopupShell>
  );
}
